/**
 * Site traffic in Amplitude (owner, 2026-10-23: "setup amplitude"), on the Conflui Amplitude project only.
 *
 * Cookieless page views and nothing else: no cookie and no stored identifier (`identityStorage: 'none'`), no user id,
 * no clicks, forms or network capture. Amplitude receives the page, its referrer and the request's country, so EU
 * visitors need no consent banner. Each page view counts on its own: unique visitors are not measured.
 *
 * It runs only when Release built a key in (VITE_AMPLITUDE_API_KEY) and only on the production app host, so dev,
 * local and preview builds of the same image are never counted.
 */
import { init } from '@amplitude/analytics-browser';

const PRODUCTION_HOSTS = new Set(['app.conflui.com']);

interface AnalyticsEnv {
  VITE_AMPLITUDE_API_KEY?: string;
  VITE_AMPLITUDE_SERVER_ZONE?: string;
}

/** Starts page-view tracking when this build and host should report; returns whether it did. */
export function startAnalytics(
  env: AnalyticsEnv = import.meta.env,
  host: string = window.location.hostname,
  start: typeof init = init,
): boolean {
  const apiKey = env.VITE_AMPLITUDE_API_KEY?.trim();
  if (!apiKey || !PRODUCTION_HOSTS.has(host)) return false;
  start(apiKey, {
    serverZone: env.VITE_AMPLITUDE_SERVER_ZONE === 'EU' ? 'EU' : 'US',
    identityStorage: 'none',
    autocapture: {
      pageViews: { trackHistoryChanges: 'all' },
      attribution: false,
      sessions: false,
      formInteractions: false,
      fileDownloads: false,
      elementInteractions: false,
      frustrationInteractions: false,
      networkTracking: false,
      webVitals: false,
      pageUrlEnrichment: false,
    },
  });
  return true;
}
